// src/components/housekeeping/AttendanceMarking.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';

function AttendanceMarking({ staff, selectedDate, userData, onUpdate }) {
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;
  const isFutureDate = selectedDate > today;
  const isPastDate = selectedDate < today;

  useEffect(() => {
    if (!isFutureDate) {
      fetchAttendance();
    }
  }, [selectedDate, staff]);

  const fetchAttendance = async () => {
    if (staff.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('housekeeping_attendance')
        .select('*')
        .eq('attendance_date', selectedDate)
        .in('staff_id', staff.map(s => s.id));

      if (error) {
        console.error('Error fetching attendance:', error);
        return;
      }

      if (data) {
        const attendanceMap = {};
        data.forEach(record => {
          attendanceMap[record.staff_id] = record;
        });
        setAttendance(attendanceMap);
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const markAttendance = async (staffId, status, checkInTime = null) => {
    // Validation 1: Cannot mark attendance for future dates
    if (isFutureDate) {
      alert('Cannot mark attendance for future dates');
      return;
    }

    // Validation 2: Only admins can modify past attendance
    if (isPastDate && !isAdmin) {
      alert('Only administrators can modify past attendance records');
      return;
    }

    // Validation 3: Warn when modifying past records
    if (isPastDate && isAdmin) {
      if (!window.confirm('You are modifying a past attendance record. Are you sure?')) {
        return;
      }
    }

    setLoading(true);
    try {
      const currentTime = new Date().toTimeString().split(' ')[0];
      const attendanceData = {
        staff_id: staffId,
        attendance_date: selectedDate,
        status,
        marked_by: userData.id,
        check_in_time: checkInTime || (status === 'present' ? currentTime : null),
        check_out_time: null,
        notes: null
      };

      if (attendance[staffId]) {
        // Update existing record
        const { data, error } = await supabase
          .from('housekeeping_attendance')
          .update({
            status,
            check_in_time: attendanceData.check_in_time,
            marked_by: userData.id
          })
          .eq('id', attendance[staffId].id)
          .select()
          .single();

        if (error) {
          console.error('Update error:', error);
          throw error;
        }

        // Update local state immediately
        setAttendance(prev => ({
          ...prev,
          [staffId]: {
            ...prev[staffId],
            ...data
          }
        }));
      } else {
        // Insert new record - no need to set created_at as it's handled by database
        const { data, error } = await supabase
          .from('housekeeping_attendance')
          .insert([attendanceData])
          .select()
          .single();

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }

        // Update local state immediately
        setAttendance(prev => ({
          ...prev,
          [staffId]: data
        }));
      }

      // Fetch fresh data
      await fetchAttendance();
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Error marking attendance:', error);
      
      // Better error messages
      if (error.code === '23505') {
        alert('Attendance already marked for this staff member. Please refresh and try again.');
      } else if (error.code === '42501') {
        alert('You do not have permission to perform this action. Please contact your administrator.');
      } else if (error.message?.includes('JWT')) {
        alert('Your session has expired. Please refresh the page and login again.');
      } else {
        alert(`Failed to mark attendance: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const markCheckOut = async (staffId) => {
    if (!attendance[staffId]) return;
    
    const checkInTime = attendance[staffId].check_in_time;
    const checkOutTime = new Date().toTimeString().split(' ')[0];
    
    // Validation: Check-out time must be after check-in time
    if (checkInTime && checkOutTime <= checkInTime) {
      alert('Check-out time must be after check-in time');
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('housekeeping_attendance')
        .update({ 
          check_out_time: checkOutTime
        })
        .eq('id', attendance[staffId].id)
        .select()
        .single();

      if (error) {
        console.error('Error marking check-out:', error);
        throw error;
      }

      // Update local state
      setAttendance(prev => ({
        ...prev,
        [staffId]: {
          ...prev[staffId],
          check_out_time: checkOutTime
        }
      }));

      await fetchAttendance();
    } catch (error) {
      console.error('Error marking check-out:', error);
      alert(`Failed to mark check-out: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      present: '#28a745',
      absent: '#dc3545',
      half_day: '#ffc107',
      leave: '#17a2b8'
    };
    return colors[status] || '#6c757d';
  };

  const canModifyAttendance = isToday || (isPastDate && isAdmin);

  return (
    <div>
      <h3 style={{ marginBottom: '20px' }}>
        Attendance for {new Date(selectedDate).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
      </h3>

      {isFutureDate && (
        <div className="alert alert-warning" style={{
          padding: '12px',
          background: '#fff3cd',
          color: '#856404',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ⚠️ Cannot mark attendance for future dates
        </div>
      )}

      {isPastDate && !isAdmin && (
        <div className="alert alert-info" style={{
          padding: '12px',
          background: '#d1ecf1',
          color: '#0c5460',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ℹ️ Past attendance records are read-only for non-admin users
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="housekeeping-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                Staff Name
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                Status
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                Check In
              </th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                Check Out
              </th>
              {canModifyAttendance && (
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {staff.map(member => {
              const record = attendance[member.id];
              const hasCheckIn = record?.check_in_time;
              const hasCheckOut = record?.check_out_time;
              
              return (
                <tr key={member.id}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                    <strong>{member.name}</strong>
                    {member.phone && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        📱 {member.phone}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                    {record ? (
                      <span className="status-badge" style={{
                        padding: '4px 12px',
                        background: getStatusColor(record.status) + '20',
                        color: getStatusColor(record.status),
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {record.status.replace('_', ' ').toUpperCase()}
                      </span>
                    ) : (
                      <span style={{ color: '#999' }}>Not Marked</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                    {hasCheckIn || '-'}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                    {hasCheckOut ? (
                      hasCheckOut
                    ) : (
                      record?.status === 'present' && isToday && hasCheckIn && (
                        <button
                          onClick={() => markCheckOut(member.id)}
                          disabled={loading}
                          className="action-btn"
                          style={{
                            padding: '4px 8px',
                            background: loading ? '#ccc' : '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          {loading ? '...' : 'Check Out'}
                        </button>
                      )
                    ) || '-'}
                  </td>
                  {canModifyAttendance && (
                    <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                      <div className="action-buttons" style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button
                          onClick={() => markAttendance(member.id, 'present')}
                          disabled={loading || isFutureDate}
                          className="action-btn present"
                          title="Present"
                          style={{
                            padding: '4px 8px',
                            background: loading ? '#ccc' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading || isFutureDate ? 'not-allowed' : 'pointer',
                            fontSize: '11px',
                            opacity: record?.status === 'present' ? 0.5 : 1
                          }}
                        >
                          P
                        </button>
                        <button
                          onClick={() => markAttendance(member.id, 'absent')}
                          disabled={loading || isFutureDate}
                          className="action-btn absent"
                          title="Absent"
                          style={{
                            padding: '4px 8px',
                            background: loading ? '#ccc' : '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading || isFutureDate ? 'not-allowed' : 'pointer',
                            fontSize: '11px',
                            opacity: record?.status === 'absent' ? 0.5 : 1
                          }}
                        >
                          A
                        </button>
                        <button
                          onClick={() => markAttendance(member.id, 'half_day')}
                          disabled={loading || isFutureDate}
                          className="action-btn half-day"
                          title="Half Day"
                          style={{
                            padding: '4px 8px',
                            background: loading ? '#ccc' : '#ffc107',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading || isFutureDate ? 'not-allowed' : 'pointer',
                            fontSize: '11px',
                            opacity: record?.status === 'half_day' ? 0.5 : 1
                          }}
                        >
                          HD
                        </button>
                        <button
                          onClick={() => markAttendance(member.id, 'leave')}
                          disabled={loading || isFutureDate}
                          className="action-btn leave"
                          title="Leave"
                          style={{
                            padding: '4px 8px',
                            background: loading ? '#ccc' : '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading || isFutureDate ? 'not-allowed' : 'pointer',
                            fontSize: '11px',
                            opacity: record?.status === 'leave' ? 0.5 : 1
                          }}
                        >
                          L
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {staff.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No staff members found. Please add staff members first.
        </div>
      )}

      {loading && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          zIndex: 9999
        }}>
          Processing...
        </div>
      )}
    </div>
  );
}

export default AttendanceMarking;